'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import whatsIncludedAdFree from '@/public/images/viverse-plus/whats-included-ad-free.png';
import whatsIncludedKidsMode from '@/public/images/viverse-plus/whats-included-kids-mode.png';
import whatsIncludedOffline from '@/public/images/viverse-plus/whats-included-offline.png';
import whatsIncludedPremiumAccess from '@/public/images/viverse-plus/whats-included-premium-access.png';
const features = [{
  key: 'ad-free',
  image: whatsIncludedAdFree
}, {
  key: 'premium-access',
  image: whatsIncludedPremiumAccess
}, {
  key: 'offline',
  image: whatsIncludedOffline
}, {
  key: 'kids-mode',
  image: whatsIncludedKidsMode
}] as const;
type Glow = {
  left: string;
  top: string;
  background: string;
  opacity: number;
};
const cardGlows: Record<(typeof features)[number]['key'], Glow[]> = {
  'ad-free': [{
    left: '42%',
    top: '30%',
    background: 'linear-gradient(135deg, #00b3e3 0%, #c761d6 100%)',
    opacity: 0.35
  }, {
    left: '58%',
    top: '58%',
    background: '#415ff8',
    opacity: 0.35
  }],
  'premium-access': [{
    left: '68%',
    top: '42%',
    background: '#71fcf7',
    opacity: 0.25
  }, {
    left: '28%',
    top: '58%',
    background: '#fe97fc',
    opacity: 0.3
  }],
  offline: [{
    left: '50%',
    top: '58%',
    background: '#415ff8',
    opacity: 0.35
  }],
  'kids-mode': [{
    left: '62%',
    top: '10%',
    background: '#c81400',
    opacity: 0.18
  }, {
    left: '42%',
    top: '72%',
    background: '#007fc8',
    opacity: 0.18
  }, {
    left: '50%',
    top: '58%',
    background: '#415ff8',
    opacity: 0.35
  }]
};
export default function WhatsIncludedSection() {
  const $ = _c(31);
  const t = useTranslations();
  let t0;
  let t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0,
      y: 16
    };
    t1 = {
      opacity: 1,
      y: 0
    };
    t2 = {
      once: true,
      amount: 0.2
    };
    t3 = {
      duration: 0.6,
      ease: "easeOut"
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("plus.whats-included.eyebrow");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <p className="text-vive-primary-40 text-xs leading-4 font-extrabold tracking-[6.15px] uppercase">{t4}</p>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("plus.whats-included.title");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <h2 className="mt-5 text-3xl leading-10 font-extrabold tracking-[0.15px] text-[#eeeef0] sm:text-[38px]">{t6}</h2>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t) {
    t8 = t("plus.whats-included.description");
    $[12] = t;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <p className="text-vive-primary-30 mt-4 max-w-xl text-[15px] leading-[26px]">{t8}</p>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t5 || $[17] !== t7 || $[18] !== t9) {
    t10 = <motion.div className="flex flex-col items-center" initial={t0} whileInView={t1} viewport={t2} transition={t3}>{t5}{t7}{t9}</motion.div>;
    $[16] = t5;
    $[17] = t7;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  let t12;
  let t13;
  let t14;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      opacity: 0,
      y: 24
    };
    t12 = {
      opacity: 1,
      y: 0
    };
    t13 = {
      once: true,
      amount: 0.2
    };
    t14 = {
      duration: 0.6,
      ease: "easeOut",
      delay: 0.12
    };
    $[20] = t11;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t11 = $[20];
    t12 = $[21];
    t13 = $[22];
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t) {
    t15 = features.map(feature => <div key={feature.key} className="relative flex flex-col items-center gap-2.5 overflow-hidden rounded-2xl bg-[rgba(10,154,226,0.06)] p-6"><div aria-hidden={true} className="pointer-events-none absolute top-0 left-1/2 size-36 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#00c8aa] opacity-20 blur-3xl" />{cardGlows[feature.key].map(_temp)}<div className="relative h-[182px] w-full max-w-[295px] overflow-hidden rounded-lg">{feature.key === "premium-access" ? <div className="absolute inset-0 flex items-center justify-center"><div className="relative h-[65px] w-[193px]"><Image src={feature.image} alt={t(`plus.whats-included.features.${feature.key}.image-alt`)} fill={true} sizes="193px" className="object-contain" /></div></div> : <Image src={feature.image} alt={t(`plus.whats-included.features.${feature.key}.image-alt`)} fill={true} sizes="295px" className="object-contain" />}</div><h3 className="relative text-sm leading-[22px] font-semibold text-white">{t(`plus.whats-included.features.${feature.key}.title`)}</h3><p className="text-vive-primary-10 relative mx-4 text-xs leading-4 tracking-[0.15px]">{t(`plus.whats-included.features.${feature.key}.description`)}</p></div>);
    $[24] = t;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  let t16;
  if ($[26] !== t15) {
    t16 = <motion.div className="mt-12 grid w-full max-w-3xl gap-6 md:grid-cols-2" initial={t11} whileInView={t12} viewport={t13} transition={t14}>{t15}</motion.div>;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== t10 || $[29] !== t16) {
    t17 = <div className="overflow-hidden px-6 py-16 text-center sm:px-12 sm:pt-10 sm:pb-20"><div className="mx-auto flex max-w-[1200px] flex-col items-center">{t10}{t16}</div></div>;
    $[28] = t10;
    $[29] = t16;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  return t17;
}
function _temp(glow, index) {
  return <div key={index} aria-hidden={true} className="pointer-events-none absolute size-48 -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl" style={{
    left: glow.left,
    top: glow.top,
    background: glow.background,
    opacity: glow.opacity
  }} />;
}